import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Loader2, History } from "lucide-react";

const ACTION_COLORS = {
  LINK: "bg-emerald-100 text-emerald-800 border-emerald-200",
  UNLINK: "bg-amber-100 text-amber-800 border-amber-200",
  REPLACE: "bg-blue-100 text-blue-800 border-blue-200",
  EDIT: "bg-slate-100 text-slate-800 border-slate-200",
  DELETE: "bg-red-100 text-red-800 border-red-200",
  IMPORT: "bg-violet-100 text-violet-800 border-violet-200",
  NOOP: "bg-slate-50 text-slate-500 border-slate-100",
};

export default function AuditTrailPanel({ cylinderId }) {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/cylinders/${cylinderId}/audit-log?limit=100`);
        setRows(data.items || []);
      } catch { setRows([]); }
    })();
  }, [cylinderId]);

  return (
    <Card className="border-slate-200" data-testid="audit-trail-panel">
      <div className="px-5 py-3 border-b border-slate-100">
        <div className="text-sm font-semibold text-slate-900 flex items-center gap-2" style={{ fontFamily: "Chivo, sans-serif" }}>
          <History className="w-4 h-4 text-red-700" />
          Audit Trail
        </div>
        <div className="text-xs text-slate-500 mt-0.5">
          Immutable log of every link, unlink, edit, and delete action. Not deletable by any user.
        </div>
      </div>
      <div className="p-5">
        {rows === null ? (
          <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
        ) : rows.length === 0 ? (
          <div className="text-sm text-slate-500 italic">No audit entries yet for this cylinder.</div>
        ) : (
          <div className="text-xs space-y-1 max-h-[420px] overflow-y-auto">
            {rows.map((r, i) => (
              <div key={i} className="grid grid-cols-[135px_75px_1fr] gap-3 items-start py-1.5 border-b border-slate-50 last:border-0" data-testid={`audit-row-${i}`}>
                <div className="text-slate-500 font-mono text-[11px]">
                  {new Date(r.at).toLocaleString()}
                </div>
                <div>
                  <span className={`inline-block text-[10px] uppercase tracking-wider font-bold border rounded px-1.5 py-0.5 ${ACTION_COLORS[r.action] || "bg-slate-100"}`}>
                    {r.action}
                  </span>
                </div>
                <div>
                  <div className="text-slate-800">
                    <span className="font-semibold">{r.entity_type.replace(/_/g, " ")}</span>
                    {r.entity_number && <span className="font-mono text-slate-500 ml-1">· {r.entity_number}</span>}
                    <span className="text-slate-400"> · {r.module}</span>
                  </div>
                  <div className="text-slate-500 text-[11px]">
                    by <span className="font-medium">{r.user_name}</span> ({r.user_role})
                    {r.remarks && <span className="ml-1 italic">— {r.remarks}</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}
